/**
 * News Page
 * Type tabs (Events / Articles) sit visually at top outside the filter panel.
 * Events tab: upcoming only, location+radius, genre, age, sort.
 * Articles tab: article type checkboxes, sort by most-viewed / length.
 * Both tabs always display newest first within their sort.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import { geocodeCityState } from '../utils/geocoding';
import Navbar from '../components/Navbar';
import SearchInput from '../components/SearchInput';
import ProfilePostCard from '../components/ProfilePostCard';
import '../styles/colors.css';

// ─── Constants ────────────────────────────────────────────────────────────────

const GENRE_LIST = [
  'Ambient', 'Alternative', 'Blues', 'Classical', 'Country', 'Cultural',
  'Dance', 'EDM', 'Electronic', 'Emo', 'Experimental', 'Folk', 'Grunge',
  'Hardcore', 'Hip-hop', 'Instrumental', 'Jazz', 'Metal', 'Noise', 'Pop',
  'Psychedelic', 'Punk', 'R&B', 'Rap', 'Reggae-Ska', 'Religious', 'Rock',
  'Shoegaze', 'Singer-Songwriter', 'Spoken Word'
];

const ARTICLE_TYPES = [
  'show review', 'scene update', 'music review', 'interview',
  'opinion', 'creative', 'release news', 'political', 'commentary', 'other'
];

const TYPE_TABS = [
  { label: 'Events', value: 'events' },
  { label: 'Articles', value: 'articles' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (str) =>
  new Date(str).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
  });

const fmtTime = (timeStr) => {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':');
  const hour = parseInt(h);
  return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
};

const fmtAge = (val) => {
  if (val === '21_plus') return '21+';
  if (val === '18_plus') return '18+';
  if (val === 'all_ages') return 'All Ages';
  return val || '';
};

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : '';

const TypeTag = ({ text }) => (
  <span style={{
    display: 'inline-block', backgroundColor: '#3a2410', color: 'white',
    padding: '2px 8px', borderRadius: '3px', fontSize: '11px',
    marginRight: '5px', marginBottom: '3px', textTransform: 'capitalize'
  }}>
    {text.replace(/_/g, ' ')}
  </span>
);

const GenreTag = ({ text }) => (
  <span style={{
    display: 'inline-block', backgroundColor: '#e0e0e0', color: '#333',
    padding: '2px 8px', borderRadius: '3px', fontSize: '11px',
    marginRight: '5px', marginBottom: '3px'
  }}>
    {text}
  </span>
);

// ─── News Page ────────────────────────────────────────────────────────────────

const NewsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Active tab — read from URL or default to articles ──
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab');
    return (tabParam === 'events' || tabParam === 'articles') ? tabParam : 'articles';
  });

  // Update URL when tab changes
  useEffect(() => {
    setSearchParams({ tab: activeTab }, { replace: true });
  }, [activeTab, setSearchParams]);

  // ── Shared filter UI state ──
  const [showFilters, setShowFilters] = useState(false);

  // ── Events filter state (load from sessionStorage if available) ──
  const [evSearchQuery, setEvSearchQuery] = useState(() => {
    const saved = sessionStorage.getItem('newsPage_eventFilters');
    return saved ? JSON.parse(saved).evSearchQuery || '' : '';
  });
  const [evSelectedGenres, setEvSelectedGenres] = useState(() => {
    const saved = sessionStorage.getItem('newsPage_eventFilters');
    return saved ? JSON.parse(saved).evSelectedGenres || [] : [];
  });
  const [evSelectedAges, setEvSelectedAges] = useState(() => {
    const saved = sessionStorage.getItem('newsPage_eventFilters');
    return saved ? JSON.parse(saved).evSelectedAges || [] : [];
  });
  const [evLocationCity, setEvLocationCity] = useState(() => {
    const saved = sessionStorage.getItem('newsPage_eventFilters');
    return saved ? JSON.parse(saved).evLocationCity || '' : (user?.city || '');
  });
  const [evLocationState, setEvLocationState] = useState(() => {
    const saved = sessionStorage.getItem('newsPage_eventFilters');
    return saved ? JSON.parse(saved).evLocationState || '' : (user?.state || '');
  });
  const [evFilterRadius, setEvFilterRadius] = useState(() => {
    const saved = sessionStorage.getItem('newsPage_eventFilters');
    return saved ? JSON.parse(saved).evFilterRadius || '' : (user?.travel_distance ? String(user.travel_distance) : '');
  });
  const [evSortBy, setEvSortBy] = useState(() => {
    const saved = sessionStorage.getItem('newsPage_eventFilters');
    return saved ? JSON.parse(saved).evSortBy || 'soonest' : 'soonest';
  });
  const [evLocationError, setEvLocationError] = useState('');
  const [evLocationLoading, setEvLocationLoading] = useState(false);
  const [evFilterLat, setEvFilterLat] = useState(() => {
    const saved = sessionStorage.getItem('newsPage_eventFilters');
    return saved ? JSON.parse(saved).evFilterLat || null : null;
  });
  const [evFilterLon, setEvFilterLon] = useState(() => {
    const saved = sessionStorage.getItem('newsPage_eventFilters');
    return saved ? JSON.parse(saved).evFilterLon || null : null;
  });

  // ── Events data ──
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState('');

  // ── Articles filter state (load from sessionStorage if available) ──
  const [arSearchQuery, setArSearchQuery] = useState(() => {
    const saved = sessionStorage.getItem('newsPage_articleFilters');
    return saved ? JSON.parse(saved).arSearchQuery || '' : '';
  });
  const [arSelectedTypes, setArSelectedTypes] = useState(() => {
    const saved = sessionStorage.getItem('newsPage_articleFilters');
    return saved ? JSON.parse(saved).arSelectedTypes || [] : [];
  });
  const [arSortBy, setArSortBy] = useState(() => {
    const saved = sessionStorage.getItem('newsPage_articleFilters');
    return saved ? JSON.parse(saved).arSortBy || 'newest' : 'newest';
  });

  // ── Articles data ──
  const [articles, setArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(true);
  const [articlesError, setArticlesError] = useState('');

  // ── Initial fetches ──
 // ── Initial fetches ──
  useEffect(() => { 
    // Auto-apply saved location filter if it exists
    if (evFilterLat && evFilterLon && evFilterRadius) {
      fetchEvents(evFilterLat, evFilterLon);
    } else {
      fetchEvents(null, null);
    }
  }, []);
  useEffect(() => { fetchArticles(); }, []);

  // Save event filters to sessionStorage whenever they change
  useEffect(() => {
    const eventFilters = {
      evSearchQuery,
      evSelectedGenres,
      evSelectedAges,
      evLocationCity,
      evLocationState,
      evFilterRadius,
      evSortBy,
      evFilterLat,
      evFilterLon
    };
    sessionStorage.setItem('newsPage_eventFilters', JSON.stringify(eventFilters));
  }, [evSearchQuery, evSelectedGenres, evSelectedAges, evLocationCity, evLocationState, evFilterRadius, evSortBy, evFilterLat, evFilterLon]);

  // Save article filters to sessionStorage whenever they change
  useEffect(() => {
    const articleFilters = {
      arSearchQuery,
      arSelectedTypes,
      arSortBy
    };
    sessionStorage.setItem('newsPage_articleFilters', JSON.stringify(articleFilters));
  }, [arSearchQuery, arSelectedTypes, arSortBy]);

  // ─── Events fetch ──────────────────────────────────────────────────────────

  const buildEventParams = (lat, lon) => {
    const params = new URLSearchParams();
    // Only upcoming events — pass a flag the backend already supports
    params.append('upcoming', 'true');
    if (evSelectedGenres.length > 0) params.append('genres', evSelectedGenres.join(','));
    if (evSelectedAges.length > 0) params.append('ageRestriction', evSelectedAges.join(','));
    // Map news sort values to backend sort values
    const sortMap = { 
      soonest: 'date', 
      popular_high: 'popular', 
      popular_low: 'popular_asc',
      distance: 'distance'
    };
    params.append('sortBy', sortMap[evSortBy] || 'date');
    if (lat && lon && evFilterRadius) {
      params.append('filterLat', lat);
      params.append('filterLon', lon);
      params.append('filterRadius', evFilterRadius);
    }
    return params.toString();
  };

  const fetchEvents = async (lat, lon) => {
    setEventsLoading(true);
    setEventsError('');
    try {
      const qs = buildEventParams(lat, lon);
      const res = await apiClient.get(`/events?${qs}`);
      if (res.data.success) {
        let data = res.data.events;
        // Client-side: keep only future/today events (backend may already do this via 'upcoming')
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        data = data.filter(e => {
          // Handle both "2026-03-07" and "2026-03-07T00:00:00.000Z" formats
          const dateOnly = e.event_date.split('T')[0]; // Extract YYYY-MM-DD part
          const [year, month, day] = dateOnly.split('-').map(Number);
          const eventDate = new Date(year, month - 1, day);
          eventDate.setHours(0, 0, 0, 0);
          return eventDate >= now;
        });
        // Client-side sort for least popular (backend doesn't have popular_asc)
        if (evSortBy === 'popular_low') {
          data = [...data].sort((a, b) => (a.going_count || 0) - (b.going_count || 0));
        }
        setEvents(data);
      }
    } catch (err) {
      setEventsError('Failed to load events');
      console.error(err);
    } finally {
      setEventsLoading(false);
    }
  };

  const handleApplyEventFilters = async () => {
    setEvLocationError('');
    if (evLocationCity && evLocationState) {
      if (!evFilterRadius) {
        setEvLocationError('Enter a radius when filtering by location.');
        return;
      }
      setEvLocationLoading(true);
      try {
        const result = await geocodeCityState(evLocationCity, evLocationState);
        setEvFilterLat(result.latitude);
        setEvFilterLon(result.longitude);
        await fetchEvents(result.latitude, result.longitude);
      } catch (err) {
        setEvLocationError(err.message);
      } finally {
        setEvLocationLoading(false);
      }
    } else {
      setEvFilterLat(null);
      setEvFilterLon(null);
      await fetchEvents(null, null);
    }
  };

  const handleEvLocationBlur = async () => {
    // Auto-apply location filter on blur if all three fields are filled
    if (evLocationCity && evLocationState && evFilterRadius) {
      if (evLocationCity.trim().length >= 2 && evLocationState.length === 2 && parseInt(evFilterRadius) > 0) {
        await handleApplyEventFilters();
      }
    } else if (!evLocationCity && !evLocationState && !evFilterRadius) {
      // Clear location filter if all fields are empty
      setEvFilterLat(null);
      setEvFilterLon(null);
      await fetchEvents(null, null);
    }
  };

  const handleClearEventFilters = () => {
    setEvSearchQuery('');
    setEvSelectedGenres([]);
    setEvSelectedAges([]);
    setEvLocationCity(user?.city || '');
    setEvLocationState(user?.state || '');
    setEvFilterRadius(user?.travel_distance ? String(user.travel_distance) : '');
    setEvSortBy('soonest');
    setEvFilterLat(null);
    setEvFilterLon(null);
    setEvLocationError('');
    fetchEvents(null, null);
  };

  const evActiveFilterCount = () => {
    let n = 0;
    if (evSearchQuery.trim()) n++;
    if (evSelectedGenres.length > 0) n++;
    if (evSelectedAges.length > 0) n++;
    if (evFilterLat && evFilterLon) n++;
    if (evSortBy !== 'soonest') n++;
    return n;
  };

  const toggleEvGenre = (g) =>
    setEvSelectedGenres(prev => prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]);
  const toggleEvAge = (a) =>
    setEvSelectedAges(prev => prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a]);

  // ─── Articles fetch ────────────────────────────────────────────────────────

  const fetchArticles = async () => {
    setArticlesLoading(true);
    setArticlesError('');
    try {
      const res = await apiClient.get('/articles');
      if (res.data.success) {
        setArticles(res.data.articles);
      }
    } catch (err) {
      setArticlesError('Failed to load articles');
      console.error(err);
    } finally {
      setArticlesLoading(false);
    }
  };

  // Apply article filters + sort client-side (all articles already fetched)
  const filteredArticles = useMemo(() => {
    let data = [...articles];

    // Search filter
    if (arSearchQuery.trim()) {
      const query = arSearchQuery.toLowerCase();
      data = data.filter(a => 
        a.description?.toLowerCase().includes(query) ||
        a.created_by_display_name?.toLowerCase().includes(query) ||
        a.collaborator_names?.some(name => name?.toLowerCase().includes(query))
      );
    }
    // Type filter
    if (arSelectedTypes.length > 0) {
      data = data.filter(a =>
        a.article_type?.some(t => arSelectedTypes.includes(t))
      );
    }

    // Sort
    if (arSortBy === 'newest') {
      data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (arSortBy === 'most_viewed') {
      data.sort((a, b) => (b.view_count || 0) - (a.view_count || 0));
    } else if (arSortBy === 'popular') {
      // view:day ratio = view_count / days_since_publication
      data.sort((a, b) => {
        const aDays = Math.max(1, Math.floor((Date.now() - new Date(a.created_at)) / (1000 * 60 * 60 * 24)));
        const bDays = Math.max(1, Math.floor((Date.now() - new Date(b.created_at)) / (1000 * 60 * 60 * 24)));
        const aRatio = (a.view_count || 0) / aDays;
        const bRatio = (b.view_count || 0) / bDays;
        return bRatio - aRatio;
      });
    }

    return data;
  }, [articles, arSearchQuery, arSelectedTypes, arSortBy]);

  const toggleArType = (t) =>
    setArSelectedTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);

  const handleClearArticleFilters = () => {
    setArSearchQuery('');
    setArSelectedTypes([]);
    setArSortBy('newest');
  };

  const arActiveFilterCount = () => {
    let n = 0;
    if (arSearchQuery.trim()) n++;
    if (arSelectedTypes.length > 0) n++;
    if (arSortBy !== 'newest') n++;
    return n;
  };

  // ─── Search Filtering ─────────────────────────────────────────────────────

  // Filter events by search query (creator + collaborator names)
  const filteredEvents = useMemo(() => {
    if (!evSearchQuery.trim()) return events;
    const query = evSearchQuery.toLowerCase();
    return events.filter(event => {
      // Search in event title
      if (event.title?.toLowerCase().includes(query)) return true;
      // Search in creator display name
      if (event.created_by_display_name?.toLowerCase().includes(query)) return true;
      // Search in collaborator names
      if (event.collaborator_names?.some(name => name?.toLowerCase().includes(query))) return true;
      return false;
    });
  }, [events, evSearchQuery]);

  // ─── Derived ──────────────────────────────────────────────────────────────

  const activeFilterCount =
    activeTab === 'events' ? evActiveFilterCount() : arActiveFilterCount();

  // ─── Render ───────────────────────────────────────────────────────────────


  return (
    <>
      <style>{`
        .news-container {
          min-height: 100vh;
          background-color: var(--bg-dark);
        }

        .news-content {
          max-width: 1100px;
          margin: 0 auto;
          padding: 140px 20px 60px;
        }

        .news-title {
          font-size: 2.6rem;
          margin: 0 0 28px 0;
          color: var(--text-light);
          text-align: center;
        }

        .type-tabs {
          display: flex;
          gap: 12px;
          margin-bottom: 24px;
          justify-content: center;
        }

        .search-sort-row {
          margin-bottom: 20px;
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
        }

        .location-bar {
          background-color: var(--smoke-gray);
          padding: 12px 24px;
          border-radius: 8px;
          display: flex;
          gap: 12px;
          align-items: center;
          flex-wrap: wrap;
          margin-bottom: 20px;
        }

        .filter-controls {
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .posts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 24px;
          margin-top: 24px;
        }

        .filter-panel {
          max-width: 800px;
          margin: 0 auto;
        }

        @media (max-width: 768px) {
          .news-content {
            padding: 125px 16px 60px 16px;
          }

          .news-title {
            font-size: 1.8rem;
            margin-bottom: 20px;
            margin-top: 24px;
          }

          .type-tabs button {
            font-size: 15px !important;
            padding: 12px 28px !important;
          }

          .search-sort-row {
            flex-direction: column;
            gap: 0;
            margin-bottom: 16px;
          }

          .search-sort-row > div:first-child {
            width: 100%;
            max-width: none !important;
          }

          .search-sort-row input {
            font-size: 16px !important;
            padding: 12px !important;
          }

          .location-bar {
            padding: 12px 8px 12px 0;
            margin-bottom: 16px;
            border-radius: 0;
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
            width: fit-content;
            max-width: 85vw;
            background-color: var(--smoke-gray);
          }

          .location-inputs {
            display: flex;
            gap: 8px;
          }

          .location-bar input {
            font-size: 16px !important;
            padding: 12px !important;
          }

          .search-container {
            max-width: 85vw !important;
            padding-right: 16px;
          }

          .filter-controls button {
            font-size: 15px !important;
            padding: 12px 20px !important;
          }

          .filter-panel {
            padding: 20px !important;
            margin-bottom: 20px !important;
            max-width: 50vw;
          }

          .filter-grid {
            flex-direction: column !important;
            gap: 20px !important;
          }

          .filter-checkboxes label {
            font-size: 15px;
          }

          .filter-checkboxes input[type="checkbox"] {
            width: 20px;
            height: 20px;
            cursor: pointer;
          }

          .genre-scroll {
            max-height: 200px !important;
          }

          .posts-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }
        }
      `}</style>

      <div className="news-container">
      <Navbar />

      <div className="news-content">

        {/* Page title */}
        <h1 className="news-title">News</h1>

        {/* ── TYPE TABS ── */}
        <div className="type-tabs">
          {TYPE_TABS.map(tab => (
            <button
              key={tab.value}
              onClick={() => {
                setActiveTab(tab.value);
                setShowFilters(false);
              }}
              className={activeTab === tab.value ? 'tab tab-dark-selected' : 'tab tab-dark-unselected'}
              style={{
                padding: '10px 32px',
                borderRadius: '30px',
                fontSize: '15px'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── SEARCH BAR ── */}
        <div className="search-row" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div className="search-container" style={{ maxWidth: '600px', flex: 1, minWidth: '250px' }}>
            {activeTab === 'events' && (
              <SearchInput
                placeholder="Search by artist/venue name..."
                onSearch={setEvSearchQuery}
                initialValue={evSearchQuery}
              />
            )}
            {activeTab === 'articles' && (
              <SearchInput
                placeholder="Search articles by title..."
                onSearch={setArSearchQuery}
                initialValue={arSearchQuery}
              />
            )}
          </div>
        </div>

        {/* ── LOCATION BAR (Events only) ── */}
        {activeTab === 'events' && (
          <div className="location-bar" style={{
            marginBottom: '16px',
            padding: '16px',
            backgroundColor: 'var(--bg-card)',
            border: '1px solid var(--border-light)',
            borderRadius: '8px',
            maxWidth: '600px',
            margin: '0 auto 16px auto',
          }}>
            <div className="location-inputs" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="City"
                value={evLocationCity}
                onChange={(e) => setEvLocationCity(e.target.value)}
                onBlur={handleEvLocationBlur}
                style={{
                  flex: '1',
                  minWidth: '60px',
                  maxWidth: '160px',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  color: 'var(--text-dark)',
                }}
              />
              <input
                type="text"
                placeholder="ST"
                value={evLocationState}
                onChange={(e) => setEvLocationState(e.target.value.toUpperCase())}
                onBlur={handleEvLocationBlur}
                maxLength="2"
                style={{
                  width: '47px',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  color: 'var(--text-dark)',
                }}
              />
              <input
                type="number"
                placeholder="Miles"
                value={evFilterRadius}
                onChange={(e) => {
                  const v = parseInt(e.target.value);
                  if (!e.target.value) setEvFilterRadius('');
                  else if (v >= 1 && v <= 999) setEvFilterRadius(String(v));
                }}
                onBlur={handleEvLocationBlur}
                style={{
                  width: '53px',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  color: 'var(--text-dark)',
                }}
              />
            </div>
            {evLocationError && <p style={{ color: 'var(--accent-danger)', fontSize: '13px', margin: '5px 0 0' }}>{evLocationError}</p>}
          </div>
        )}

        {/* ── FILTER TOGGLE + CLEAR ALL ── */}
        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn btn-primary"
            style={{
              padding: '10px 24px',
              fontWeight: (activeTab === 'events' ? evActiveFilterCount() : arActiveFilterCount()) > 0 ? '700' : '600',
              opacity: (activeTab === 'events' ? evActiveFilterCount() : arActiveFilterCount()) > 0 ? 1 : 0.8
            }}
          >
            {showFilters ? 'Hide Filters' : `Filters${(activeTab === 'events' ? evActiveFilterCount() : arActiveFilterCount()) > 0 ? ` (${(activeTab === 'events' ? evActiveFilterCount() : arActiveFilterCount())})` : ''}`}
          </button>

          {activeTab === 'events' && evActiveFilterCount() > 0 && (
            <button onClick={handleClearEventFilters} className="btn btn-danger" style={{ padding: '10px 24px' }}>
              Clear All
            </button>
          )}
          {activeTab === 'articles' && arActiveFilterCount() > 0 && (
            <button onClick={handleClearArticleFilters} className="btn btn-danger" style={{ padding: '10px 24px' }}>
              Clear All
            </button>
          )}
        </div>

        {/* ── EVENTS FILTER PANEL ── */}
        {showFilters && activeTab === 'events' && (
          <div className="card" style={{ padding: '20px', marginBottom: '24px' }}>

            <div className="filter-grid" style={{ display: 'flex', gap: '40px', flexWrap: 'wrap', marginBottom: '20px' }}>

              {/* Sort */}
              <div style={{ width: '100%' }}>
                <label style={{ fontWeight: '700', display: 'block', marginBottom: '8px', color: 'var(--text-dark)' }}>Sort</label>
                <select
                  value={evSortBy}
                  onChange={(e) => setEvSortBy(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '4px',
                    border: '1px solid var(--border-light)',
                    fontSize: '15px',
                    backgroundColor: 'var(--bg-card)',
                    color: 'var(--text-dark)',
                    cursor: 'pointer'
                  }}
                >
                  <option value="soonest">Soonest</option>
                  <option value="popular_high">Most Popular</option>
                  <option value="popular_low">Least Popular</option>
                  {evFilterLat && evFilterLon && <option value="distance">Distance</option>}
                </select>
              </div>

              {/* Genres */}
              <div>
                <label style={{ fontWeight: '700', display: 'block', marginBottom: '8px', color: 'var(--text-dark)' }}>
                  Genre {evSelectedGenres.length > 0 && <span style={{ color: 'var(--accent-primary)' }}>({evSelectedGenres.length} selected)</span>}
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '220px', overflowY: 'auto', paddingRight: '8px' }}>
                  {GENRE_LIST.map(g => (
                    <label key={g} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '13px', color: 'var(--text-dark)' }}>
                      <input type="checkbox" checked={evSelectedGenres.includes(g)} onChange={() => toggleEvGenre(g)} />
                      {g}
                    </label>
                  ))}
                </div>
              </div>

              {/* Age restriction */}
              <div>
                <label style={{ fontWeight: '700', display: 'block', marginBottom: '8px', color: 'var(--text-dark)' }}>Age Restriction</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                  {['all_ages', '18_plus', '21_plus'].map(a => (
                    <label key={a} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '13px', color: 'var(--text-dark)' }}>
                      <input type="checkbox" checked={evSelectedAges.includes(a)} onChange={() => toggleEvAge(a)} />
                      {fmtAge(a)}
                    </label>
                  ))}
                </div>
              </div>

            </div>

            <button
              onClick={handleApplyEventFilters}
              disabled={evLocationLoading}
              className="btn btn-primary"
              style={{
                padding: '10px 30px',
                cursor: evLocationLoading ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                opacity: evLocationLoading ? 0.6 : 1
              }}
            >
              {evLocationLoading ? 'Locating...' : 'Apply Filters'}
            </button>
          </div>
        )}

        {/* ── ARTICLES FILTER PANEL ── */}
        {showFilters && activeTab === 'articles' && (
          <div className="card" style={{ padding: '20px', marginBottom: '24px' }}>

            {/* Sort */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontWeight: '700', display: 'block', marginBottom: '8px', color: 'var(--text-dark)' }}>Sort</label>
              <select
                value={arSortBy}
                onChange={(e) => setArSortBy(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-light)',
                  fontSize: '15px',
                  backgroundColor: 'var(--bg-card)',
                  color: 'var(--text-dark)',
                  cursor: 'pointer'
                }}
              >
                <option value="newest">Newest</option>
                <option value="most_viewed">Most Viewed</option>
                <option value="popular">Popular (views/day)</option>
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontWeight: '700', display: 'block', marginBottom: '8px', color: 'var(--text-dark)' }}>
                Article Type {arSelectedTypes.length > 0 && <span style={{ color: 'var(--accent-primary)' }}>({arSelectedTypes.length} selected)</span>}
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '6px' }}>
                {ARTICLE_TYPES.map(type => (
                  <label key={type} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--text-dark)', textTransform: 'capitalize' }}>
                    <input type="checkbox" checked={arSelectedTypes.includes(type)} onChange={() => toggleArType(type)} />
                    {type}
                  </label>
                ))}
              </div>
            </div>

            <button
              onClick={handleClearArticleFilters}
              className="btn btn-primary"
              style={{ padding: '10px 30px', fontSize: '16px' }}
            >
              Apply Filters
            </button>
          </div>
        )}

        {/* ── RESULTS COUNT ── */}
        {activeTab === 'events' && !eventsLoading && (
          <p style={{ color: 'var(--text-light)', marginBottom: '15px', opacity: 0.8, textAlign: 'center' }}>
            {filteredEvents.length === 0 
              ? 'No upcoming events found' 
              : `${filteredEvents.length} upcoming event${filteredEvents.length !== 1 ? 's' : ''} found`}
          </p>
        )}
        {activeTab === 'articles' && !articlesLoading && (
          <p style={{ color: 'var(--text-light)', marginBottom: '15px', opacity: 0.8, textAlign: 'center' }}>
            {filteredArticles.length === 0 
              ? 'No articles found' 
              : `${filteredArticles.length} article${filteredArticles.length !== 1 ? 's' : ''} found`}
          </p>
        )}

        {/* ── EVENTS LIST ── */}
        {activeTab === 'events' && (
          eventsLoading ? (
            <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text-light)' }}>Loading events...</div>
          ) : eventsError ? (
            <div style={{ padding: '60px', textAlign: 'center' }}>
              <p style={{ color: 'var(--accent-danger)' }}>{eventsError}</p>
              <button onClick={fetchEvents} className="btn btn-primary">Try Again</button>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px' }}>
              <p style={{ color: 'var(--text-light)' }}>No upcoming events match your filters.</p>
              {evActiveFilterCount() > 0 && (
                <button onClick={handleClearEventFilters} className="btn btn-primary" style={{ marginTop: '12px' }}>
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
              {filteredEvents.map(event => (
                <ProfilePostCard
                  key={event.id}
                  post={{ ...event, post_type: 'event' }}
                />
              ))}
            </div>
          )
        )}

        {/* ── ARTICLES LIST ── */}
        {activeTab === 'articles' && (
          articlesLoading ? (
            <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text-light)' }}>Loading articles...</div>
          ) : articlesError ? (
            <div style={{ padding: '60px', textAlign: 'center' }}>
              <p style={{ color: 'var(--accent-danger)' }}>{articlesError}</p>
              <button onClick={fetchArticles} className="btn btn-primary">Try Again</button>
            </div>
          ) : filteredArticles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px' }}>
              <p style={{ color: 'var(--text-light)' }}>No articles match your filters.</p>
              {arActiveFilterCount() > 0 && (
                <button onClick={handleClearArticleFilters} className="btn btn-primary" style={{ marginTop: '12px' }}>
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
              {filteredArticles.map(article => (
                <ProfilePostCard
                  key={article.id}
                  post={{ ...article, post_type: 'article' }}
                />
              ))}
            </div>
          )
        )}

      </div>
    </div>
</>
  );
};

export default NewsPage;
